import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneRight, Phone, CheckCircle } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Your Name", placeholder: "John D.", type: "text" },
  { name: "phone", label: "Callback Number", placeholder: "803-555-0199", type: "tel" },
  { name: "location", label: "Your Location", placeholder: "I-77 exit 82, Rock Hill", type: "text" },
  { name: "vehicle", label: "Vehicle", placeholder: "2019 Silver Audi A5", type: "text" },
];

export const DispatchForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", location: "", vehicle: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location || !form.vehicle) {
      toast.error("Please fill in name, number, location and vehicle.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received — the owner will call you back shortly.");
      setForm({ name: "", phone: "", location: "", vehicle: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="dispatch"
      data-testid="dispatch-section"
      className="relative z-10 bg-[var(--tt-black)] px-5 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--tt-green)] mb-5">
              / Request a Dispatch
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-6">
              Can't call right now? <br />
              <span className="text-zinc-500">Send it. We'll ring you.</span>
            </h2>
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md mb-8">
              Drop your details and the owner calls you straight back during our 8am–6pm hours.
              Every request lands with the driver, not a call center.
            </p>
            <a
              href={PHONE_TEL}
              data-testid="dispatch-call-fallback"
              className="inline-flex items-center gap-3 font-display uppercase text-xl text-white hover:text-[var(--tt-green)] transition-colors"
            >
              <Phone weight="fill" className="h-6 w-6 text-[var(--tt-green)]" />
              Or call {PHONE_DISPLAY}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative bg-[var(--tt-surface)] border border-white/10 p-7 md:p-10 tt-clip-card">
            {done ? (
              <div
                data-testid="dispatch-success"
                className="flex flex-col items-center text-center py-14 gap-5"
              >
                <CheckCircle weight="fill" className="h-16 w-16 text-[var(--tt-green)] tt-green-glow" />
                <h3 className="font-display uppercase text-3xl tracking-tight">Request Sent</h3>
                <p className="text-zinc-400 max-w-xs">
                  Hang tight — the owner will call you back shortly. For an active emergency, call
                  now.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="dispatch-reset"
                  className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-[var(--tt-green)] transition-colors"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="dispatch-form" className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {FIELDS.map((f) => (
                    <div key={f.name} className="flex flex-col gap-2">
                      <label
                        htmlFor={f.name}
                        className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500"
                      >
                        {f.label}
                      </label>
                      <input
                        id={f.name}
                        data-testid={`dispatch-input-${f.name}`}
                        type={f.type}
                        value={form[f.name]}
                        onChange={update(f.name)}
                        placeholder={f.placeholder}
                        className="bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[var(--tt-green)] focus:outline-none focus:shadow-[0_0_14px_rgba(57,255,20,0.2)] transition-[border-color,box-shadow]"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="notes"
                    className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500"
                  >
                    What's going on? (optional)
                  </label>
                  <textarea
                    id="notes"
                    data-testid="dispatch-input-notes"
                    rows={3}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="Flat bed needed, car won't start, blocking traffic…"
                    className="bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[var(--tt-green)] focus:outline-none resize-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="dispatch-submit"
                  className="group mt-2 flex items-center justify-center gap-3 bg-[var(--tt-green)] text-black font-display uppercase text-xl px-8 py-4 border-2 border-[var(--tt-green)] hover:bg-transparent hover:text-[var(--tt-green)] hover:shadow-[0_0_24px_rgba(57,255,20,0.45)] transition-[background-color,color,box-shadow] duration-300 disabled:opacity-60 disabled:cursor-not-allowed tt-clip-card"
                >
                  {submitting ? "Sending…" : "Send Dispatch Request"}
                  {!submitting && (
                    <PaperPlaneRight weight="fill" className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
